import MaterialBuilder from "../rendering/material-builder";
import RenderManager from "../rendering/render-manager";
import SlideManager from "../utils/slide-manager";
import FBXLoader from "../loaders/fbx-loader";

import { fire } from "../utils/events";
import getPersistentUrl from "../utils/persistent-path";
import AssetLibrary from "../assets/asset-library";

let gltfLoader, fbxLoader;

const BASE_COLOR = 0xaaaaaa;

const BASE_THICKNESS = 0.1;
const COLLIDER_SIZE = 0.4;

const MIN_VALUE = 0.01;
const MAX_VALUE = 5;
const MIN_DEFAULT_VALUE = 1.5;
const MAX_DEFAULT_VALUE = 2;

const ORIGINAL_WIDTH = false;

const BOX_COLOR = 0x006600;
const BOX_HL_COLOR = 0x006666;
const BOXMODEL_SIZE = 0.5;

const mmmObjects = new Set();

const MaterialCopyProperties = [
    'alphaMap', 'alphaTest', 'aoMap', 'aoMapIntensity', 'blendDst', 'blendDstAlpha', 'blendEquation', 'blendEquationAlpha',
    'blendSrc', 'blendSrcAlpha', 'blending', 'bumpMap', 'bumpScale', 'clipIntersection', 'clipShadows', 'clippingPlanes',
    'color', 'colorWrite', 'depthFunc', 'depthTest', 'depthWrite', 'displacementBias', 'displacementMap', 'displacementScale',
    'dithering', 'emissive', 'emissiveIntensity', 'emissiveMap', 'envMap', 'envMapIntensity', 'flatShading', 'fog', 'lightMap',
    'lightMapIntensity', 'lights', 'map', 'metalness', 'metalnessMap', 'morphNormals', 'morphTargets', 'name', 'normalMap',
    'normalMapType', 'normalScale', 'opacity', 'overdraw', 'polygonOffset', 'polygonOffsetFactor', 'polygonOffsetUnits',
    'precision', 'premultipliedAlpha', 'refractionRatio', 'roughness', 'roughnessMap', 'shadowSide', 'side', 'skinning',
    'transparent', 'vertexColors', 'visible', 'wireframe', 'wireframeLinecap', 'wireframeLinejoin', 'wireframeLinewidth'
];

const modelUrlBase = "meanMedianMode/";
const models = [{
    name: "Alien Goblin",
    url: "AlienGoblin.gltf",
    loadFunction: loadGltf,
}, {
    name: "Alien Goblin (Orange)",
    url: "AlienGoblin_Orange.gltf",
    loadFunction: loadGltf,
}, {
    name: "Astronaut",
    url: "Astronaut.fbx",
    loadFunction: loadFbx,
}, {
    name: "Blue Alien",
    url: "Bluealien.gltf",
    loadFunction: loadGltf,
}, {
    name: "Dennis",
    url: "Dennis.gltf",
    loadFunction: loadGltf,
}, {
    name: "Guy",
    url: "Guy.gltf",
    loadFunction: loadGltf,
}, {
    name: "Lady Chef",
    url: "LadyChef.gltf",
    loadFunction: loadGltf,
}, {
    name: "Lady",
    url: "Lady.gltf",
    loadFunction: loadGltf,
}, {
    name: "Minotaur",
    url: "Minotaur.gltf",
    loadFunction: loadGltf,
}, {
    name: "Penguin",
    url: "Penguin.gltf",
    loadFunction: loadGltf,
}, {
    name: "Snowman",
    url: "Snowman.gltf",
    loadFunction: loadGltf,
}, {
    name: "Tattoo guy",
    url: "Tattoo guy.gltf",
    loadFunction: loadGltf,
}, {
    name: "Treeman",
    url: "Treeman.gltf",
    loadFunction: loadGltf,
}];

class MeanMedianMode extends THREE.Object3D {
    constructor({ dragCallback }) {
        super();

        this.isMeanMedianMode = true;
        this.type = "MeanMedianMode";

        this._params = {
            size: [3, 3],
            values: [],
            showInfo: true,
            modelIds: [],
            spacing: 1.0,
            showAllHeights: false,
            showCalc: false,
            showParams: {
                mean: true,
                median: true,
                mode: true,
            },
        };

        this._statValues = {
            mean: 0,
            median: 0,
            mode: [],
        };

        this._updateModelIds();

        this._dragging = null;
        this._mouseDown = false;
        this._hoveredIndex = null;
        this._highlightedIndex = null;

        this.dragCallback = dragCallback;

        let baseMaterial = MaterialBuilder.build({ color: BASE_COLOR, materialType: "pbr" });
        this.boxGeometry = new THREE.BoxBufferGeometry(1, 1, 1);
        this.baseBox = new THREE.Mesh(this.boxGeometry, baseMaterial);
        this.baseBox.position.set(0, -0.5 * BASE_THICKNESS, 0);
        this._updateBaseSize();
        this.baseBox.dontShowInHierarchyPanel = true;
        this.add(this.baseBox);

        this.colliderGeometry = new THREE.BoxBufferGeometry(COLLIDER_SIZE, 1, COLLIDER_SIZE);
        this.colliderMaterial = new THREE.MeshBasicMaterial({ visible: false });

        this._domElement = document.createElement("div");
        this._domElement.classList.add("mean-median-mode-cnt");
        RenderManager.overlay2d.appendChild(this._domElement);

        this._valuesDisplayCnt = document.createElement("div");
        this._valuesDisplayCnt.classList.add("mean-median-mode-values-overlay");
        this._domElement.appendChild(this._valuesDisplayCnt);

        this.sample = [];
        this.unitIndex = new WeakMap();
        this.objectUnit = new WeakMap();

        this._rebuildSample();

        this._updateStatValues();

        let visible = this.visible;
        Object.defineProperty(this, "visible", {
            get: () => visible,
            set: (enabled) => {
                visible = enabled;
                this._valuesDisplayCnt.classList.toggle("hidden", !enabled);

                RenderManager.activeCamera.update = true;
                fire('mmm_toggle_visibility', this);
            },
        });

        mmmObjects.add(this);
    }

    get showInfo() {
        return this._params.showInfo;
    }

    set showInfo(enabled) {
        this._params.showInfo = enabled;

        RenderManager.activeCamera.update = true;
        fire('mmm_toggle_info', this);
    }

    get showAllHeights() {
        return this._params.showAllHeights;
    }

    set showAllHeights(show) {
        this._params.showAllHeights = show;
        for (let unit of this.sample) {
            unit.forceShowValue(show);
        }
    }

    get showCalc() {
        return this._params.showCalc;
    }

    set showCalc(enabled) {
        this._params.showCalc = enabled;

        RenderManager.activeCamera.update = true;
        fire('mmm_toggle_calc', this);
    }

    getSpacing() {
        return this._params.spacing;
    }

    setSpacing(spacing) {
        this._params.spacing = spacing;

        this._updateBaseSize();
        this._updateUnitPositions();

        RenderManager.activeCamera.update = true;
    }

    _updateModelIds() {
        let modelIds = this._params.modelIds;
        let count = this._params.values.length;

        while (modelIds.length < count) {
            let modelId = modelIds.length % models.length;
            modelIds.push(modelId);
        }
        modelIds.length = count;
    }

    setSampleSize(newSize) {
        let RANGE_SIZE = MAX_DEFAULT_VALUE - MIN_DEFAULT_VALUE;

        let values = this._params.values;
        let oldSize = values.length;
        for (let i = oldSize; i < newSize; i++) {
            let value = MIN_DEFAULT_VALUE + Math.random() * RANGE_SIZE;
            value = Math.round(value * 100) * 0.01;
            values[i] = value;
        }
        values.length = newSize;

        this._updateModelIds();
        this._updateSampleSize();
        this._updateUnitPositions();
        this._updateUnitSizes();

        this._updateStatValues();

        RenderManager.activeCamera.update = true;
        fire('mmm_change_values', this);
    }

    getSampleSize() {
        return this._params.values.length;
    }

    setValues(values) {
        this.setSampleSize(values.length);
        for (let i = 0; i < values.length; i++) {
            this._setValue(i, values[i]);
        }

        this._updateStatValues();

        RenderManager.activeCamera.update = true;
        fire('mmm_change_values', this);
    }

    getValues() {
        return this._params.values;
    }

    get statValues() {
        return this._statValues;
    }

    setModelIds(ids) {
        let modelIds = this._params.modelIds;
        for (let i = 0; i < ids.length; i++) {
            modelIds[i] = ids[i];
        }

        this._updateModelIds();
        this._rebuildSample();

        RenderManager.activeCamera.update = true;
    }

    getModelIds() {
        return this._params.modelIds;
    }

    setValue(index, value) {
        this._setValue(index, value);
        this._updateStatValues();

        RenderManager.activeCamera.update = true;
        fire('mmm_change_values', this);
    }

    getGridSize() {
        return this._params.size;
    }

    setGridSize(size) {
        this._params.size[0] = size[0];
        this._params.size[1] = size[1];

        this._updateBaseSize();
        this._updateUnitPositions();

        RenderManager.activeCamera.update = true;
    }

    _updateUnitSizes() {
        let values = this._params.values;
        let units = this.sample;

        for (let i = 0; i < values.length; i++) {
            units[i].setValue(values[i]);
        }
    }

    _setValue(index, value) {
        this._params.values[index] = value;
        let unit = this.sample[index];
        unit.setValue(value);

        if (this.setValueCallback) {
            this.setValueCallback(this, index, value);
        }
    }

    _makeUnitModel(modelId) {
        return new Unit({
            modelId: modelId,
            colliderGeometry: this.colliderGeometry,
            colliderMaterial: this.colliderMaterial,
            objectUnit: this.objectUnit,
            forceShowLabel: this._params.showAllHeights,
        });
    }

    _rebuildSample() {
        for (let unit of this.sample) {
            this.remove(unit.root);
            this._valuesDisplayCnt.removeChild(unit.valueLabel);
        }
        this.sample.length = 0;

        this._updateSampleSize();
        this._updateUnitPositions();
        this._updateUnitValues();
    }

    _updateBaseSize() {
        let [w, h] = this._params.size;
        let spacing = this._params.spacing;
        this.baseBox.scale.set(w * spacing, BASE_THICKNESS, h * spacing);
    }

    _updateSampleSize() {
        let values = this._params.values;
        let units = this.sample;
        let modelIds = this._params.modelIds;

        this._updateModelIds();

        let oldCount = units.length, newCount = values.length;
        if (oldCount < newCount) {
            for (let i = oldCount; i < newCount; i++) {
                let unit = this._makeUnitModel(modelIds[i]);
                this.add(unit.root);
                this._valuesDisplayCnt.appendChild(unit.valueLabel);
                units[i] = unit;
                this.unitIndex.set(unit, i);
            }
        } else {
            for (let i = newCount; i < oldCount; i++) {
                let unit = units[i];
                this.remove(unit.root);
                this._valuesDisplayCnt.removeChild(unit.valueLabel);
            }
            units.length = newCount;
        }
    }

    _updateUnitPositions() {
        let [w, h] = this._params.size;
        let spacing = this._params.spacing;

        for (let i = 0; i < this._params.values.length; i++) {
            let [x, y] = [i % w + 0.5, Math.floor(i / w) + 0.5];
            let unit = this.sample[i];
            unit.root.position.set((w * 0.5 - x) * spacing, 0, (h * 0.5 - y) * spacing);
        }
    }

    _updateUnitValues() {
        let values = this._params.values;
        let units = this.sample;

        for (let i = 0; i < values.length; i++) {
            units[i].setValue(values[i]);
        }
    }

    _updateStatValues() {
        let values = this._params.values;

        let mean = 0;
        for (let value of values) {
            mean += value;
        }
        mean /= values.length;
        this._statValues.mean = mean;

        let median;
        let sortedValues = values.slice();
        sortedValues.sort((a, b) => a - b);
        if (sortedValues.length % 2 == 1) {
            median = sortedValues[Math.floor(sortedValues.length / 2)];
        } else {
            let mid = sortedValues.length / 2;
            median = (sortedValues[mid - 1] + sortedValues[mid]) * 0.5;
        }
        this._statValues.median = median;

        let mode = [];
        let valTimes = new Map();
        for (let value of values) {
            value = Math.round(value * 100) * 0.01;
            valTimes.set(value, (valTimes.get(value) || 0) + 1);
        }
        let modeTimes = 0;
        for (let [value, times] of valTimes) {
            modeTimes = Math.max(modeTimes, times);
        }
        if (modeTimes > 1) {
            for (let [value, times] of valTimes) {
                if (times == modeTimes) {
                    mode.push(value);
                }
            }
        }
        mode.sort((a, b) => a - b);
        this._statValues.mode = mode;
    }

    showParam(name, show) {
        this._params.showParams[name] = show;

        RenderManager.activeCamera.update = true;
        fire('mmm_toggle_param', this);
    }

    isParamShown(name) {
        return this._params.showParams[name];
    }

    _dragStart(index, point) {
        this._dragging = {
            index: index,
            startValue: this._params.values[index],
            startPoint: point.clone(),
        };

        this.sample[index].showValue(true);
    }

    _dragMove(ray) {
        let { index, startValue, startPoint } = this._dragging;
        let unit = this.sample[index];
        if (!unit) {
            this._dragging = null;
            return;
        }

        RenderManager.activeCamera.update = true;

        let up = new THREE.Vector3(0, 1, 0);
        up.transformDirection(unit.root.matrixWorld);

        let rayDir = ray.direction;
        let normal = new THREE.Vector3().crossVectors(rayDir, up);

        let crossMatrix = new THREE.Matrix4().set(
            rayDir.x, up.x, normal.x, 0,
            rayDir.y, up.y, normal.y, 0,
            rayDir.z, up.z, normal.z, 0,
            0, 0, 0, 1
        );
        let crossInverse = new THREE.Matrix4().getInverse(crossMatrix);

        let startRelative = startPoint.clone().applyMatrix4(crossInverse);
        let rayRelative = ray.origin.clone().applyMatrix4(crossInverse);
        let crossPoint = new THREE.Vector3(startRelative.x, rayRelative.y, startRelative.z).applyMatrix4(crossMatrix);

        let startDist = up.dot(startPoint);
        let crossDist = up.dot(crossPoint);
        let newValue = startValue + crossDist - startDist;

        newValue = Math.min(Math.max(newValue, MIN_VALUE), MAX_VALUE);;
        newValue = Math.round(newValue * 100) * 0.01;
        this.setValue(index, newValue);

        if (this.dragCallback) {
            this.dragCallback(this, index, newValue);
        }
    }

    _dragEnd() {
        let dragging = this._dragging;
        if (dragging == null) return;

        let unit = this.sample[dragging.index];
        if (unit) unit.setValueHideTimeout(500);

        this._dragging = null;
    }

    updateInput(mouse, camera, controls) {
        let raycaster;
        let intersection;

        if (this.visible) {
            raycaster = new THREE.Raycaster();
            raycaster.setFromCamera(mouse.position, camera);

            intersection = raycaster.intersectObjects(this.sample.map(unit => unit.root), true);
            intersection = intersection && intersection[0];
        }

        let hoverPoint;
        if (intersection) {
            let unit = this.objectUnit.get(intersection.object);
            this._hoveredIndex = this.unitIndex.has(unit) ? this.unitIndex.get(unit) : null;
            hoverPoint = intersection.point;
        } else {
            this._hoveredIndex = null;
        }

        if (!this._mouseDown && mouse.which.left) {
            this._mouseDown = true;
            if (this._hoveredIndex != null) {
                this._dragStart(this._hoveredIndex, hoverPoint);
                controls.enabled = false;
                RenderManager.activeCamera.update = true;
            }
        }

        if (this._mouseDown && !mouse.which.left) {
            this._mouseDown = false;
            if (this._dragging) {
                if (!SlideManager.currentSlide.camera.disabled) {
                    controls.enabled = true;
                }
                this._dragEnd();
                RenderManager.activeCamera.update = true;
            }
        }

        if (this._dragging && raycaster) {
            this._dragMove(raycaster.ray);
        }
    }

    updateHighlighting() {
        let oldHlIndex = this._highlightedIndex;
        let newHlIndex = this._dragging ? this._dragging.index : this._hoveredIndex;
        if (newHlIndex === oldHlIndex) return;

        if (oldHlIndex !== null) {
            let unit = this.sample[oldHlIndex];
            if (unit) unit.setHighlighting(false);
        }

        if (newHlIndex !== null) {
            let unit = this.sample[newHlIndex];
            if (unit) unit.setHighlighting(true);
        }

        this._highlightedIndex = newHlIndex;
        RenderManager.activeCamera.update = true;
    }

    updateLabelPositions(camera) {
        for (let unit of this.sample) {
            unit.updateLabelPosition(camera);
        }
    }
};

class Unit {
    constructor({ modelId, colliderGeometry, colliderMaterial, objectUnit, forceShowLabel }) {
        this.properties = {
            modelId: modelId,
            colliderGeometry: colliderGeometry,
            colliderMaterial: colliderMaterial,
            scale: 1,
            forceShowLabel: forceShowLabel,
        };

        this.root = new THREE.Group();
        this.scaler = new THREE.Group();
        this.colScaler = new THREE.Group();
        //this.root.add(this.scaler, this.colScaler);
        this.root.add(this.scaler);

        this.collider = new THREE.Mesh(colliderGeometry, colliderMaterial);
        this.collider.position.y = 0.5;
        this.colScaler.add(this.collider);

        this.root.dontShowInHierarchyPanel = true;

        this.value = 1;
        this.displayValueActive = false;
        this.labelBeyondCamera = false;

        this.visualObject = null;
        let promise = loadModel(modelId);
        promise.then(obj => {
            obj = this._cloneHierarchy(obj);
            let box = new THREE.Box3().setFromObject(obj);
            obj.position.y = -box.min.y;
            this.properties.height = box.max.y - box.min.y;
            this._applyObjectScale();
            this._prepareMaterials(obj);

            this.visualObject = new THREE.Object3D();
            this.visualObject.add(obj);
            this.scaler.add(this.visualObject);

            obj.traverse(subObj => objectUnit.set(subObj, this));
            RenderManager.activeCamera.update = true;
        });

        this.valueLabel = document.createElement("div");
        this.valueLabel.classList.add("mean-median-mode-value");
        this.displayValue(false);
        this.labelHideTimeout = null;
    }

    _cloneHierarchy(oldRoot) {
        let newRoot = oldRoot.clone();

        let oldObjects = [], newObjects = [];
        oldRoot.traverse(obj => oldObjects.push(obj));
        newRoot.traverse(obj => newObjects.push(obj));

        let mapObjects = new WeakMap();
        for (let i = 0; i < oldObjects.length; i++) {
            mapObjects.set(oldObjects[i], newObjects[i]);
        }

        for (let oldObject of oldObjects) {
            if (oldObject instanceof THREE.SkinnedMesh) {
                let oldSkeleton = oldObject.skeleton;
                let newObject = mapObjects.get(oldObject), newSkeleton = oldSkeleton.clone();
                for (let i = 0; i < newSkeleton.bones.length; i++) {
                    newSkeleton.bones[i] = mapObjects.get(newSkeleton.bones[i]);
                }
                newObject.bind(newSkeleton);

                let oldInverses = oldSkeleton.boneInverses, newInverses = newSkeleton.boneInverses;
                for (let i = 0; i < oldInverses.length; i++) {
                    newInverses[i].copy(oldInverses[i]);
                }
            }
        }

        return newRoot;
    }

    _prepareMaterials(root) {
        this.objectMaterials = new Map();
        this.materialsHighlighted = new Map();

        root.traverse(obj => {
            if (obj.material) {
                this.objectMaterials.set(obj, obj.material);

                if (!this.materialsHighlighted.has(obj.material)) {
                    this.materialsHighlighted.set(obj.material, this._makeHighlightedMaterial(obj.material));
                }
            }
        });
    }

    _makeHighlightedMaterial(material) {
        if (material instanceof Array) {
            let hlMaterial = [];
            for (let singleMat of material) {
                hlMaterial.push(this._makeHighlightedMaterial(singleMat));
            }
            return hlMaterial;
        } else {
            let hlMaterial = material.clone();
            hlMaterial.emissiveIntensity = 1;
            hlMaterial.emissive.setRGB(0, 0, 0.25);
            return hlMaterial;
        }
    }

    setHighlighting(enabled) {
        if (!this.visualObject) return;

        this.visualObject.traverse(obj => {
            if (!this.objectMaterials.has(obj)) return;

            let material = this.objectMaterials.get(obj);
            if (enabled) material = this.materialsHighlighted.get(material);
            obj.material = material;
        });
    }

    setValue(value) {
        this.value = value;
        this._applyObjectScale();
        this.valueLabel.innerText = (value * 100).toFixed() + 'cm';
    }

    _applyObjectScale() {
        let v = this.value;
        let width = (MIN_DEFAULT_VALUE + MAX_DEFAULT_VALUE) * 0.5;
        this.colScaler.scale.set(width, v, width);
        if (ORIGINAL_WIDTH) {
            this.scaler.scale.set(1, v / this.properties.height, 1);
        } else {
            this.scaler.scale.set(width, v, width).divideScalar(this.properties.height);
        }
    }

    displayValue(enabled) {
        this.displayValueActive = enabled;
        this._updateShowValue();
    }

    showValue() {
        this.displayValue(true);

        if (this.labelHideTimeout !== null) {
            clearTimeout(this.labelHideTimeout);
            this.labelHideTimeout = null;
        }
    }

    setValueHideTimeout(time) {
        if (this.labelHideTimeout !== null) {
            clearTimeout(this.labelHideTimeout);
        }

        this.labelHideTimeout = setTimeout(() => {
            this.displayValue(false);
            this.labelHideTimeout = null;
        }, time);
    }

    forceShowValue(force) {
        this.properties.forceShowLabel = force;
        this._updateShowValue();
    }

    _updateShowValue() {
        let show = !this.labelBeyondCamera && (this.displayValueActive || this.properties.forceShowLabel);
        this.valueLabel.classList.toggle("hidden", !show);
    }

    clone() {
        return new this.constructor(this.properties);
    }

    updateLabelPosition(camera) {
        let pos = new THREE.Vector4(0, this.properties.height, 0, 1);
        pos.applyMatrix4(this.scaler.matrixWorld);
        pos.applyMatrix4(camera.matrixWorldInverse).applyMatrix4(camera.projectionMatrix);

        this.labelBeyondCamera = pos.z < 0;
        if (!this.labelBeyondCamera) {
            let z = pos.z;
            pos.divideScalar(pos.w);
            pos.x = (pos.x + 1) * 0.5;
            pos.y = (-pos.y + 1) * 0.5;
            this.valueLabel.style.zIndex = Math.floor(-z * 1000);
            this.valueLabel.style.left = (100 * pos.x) + '%';
            this.valueLabel.style.top = (100 * pos.y) + '%';
        }

        this._updateShowValue();
    }
}

let modelPromise = [];

function loadModel(index) {
    let promise = modelPromise[index];
    if (promise) return promise;

    let modelInfo = models[index];
    promise = modelInfo.loadFunction(getPersistentUrl(modelUrlBase + modelInfo.url));
    modelPromise[index] = promise;
    return promise;
}

function updateDataValuesOnChange(object, index, value) {
    let mmmData = Q3.data.assets.objects[object.uuid];
    if (!mmmData) return;

    mmmData.meanMedianMode.values[index] = value;
}

function updateMMMObjects(mouse, camera, controls) {
    for (let mmm of mmmObjects) {
        if (mmm.uuid in Q3.data.assets.objects) {
            mmm.updateInput(mouse, camera, controls);
            if (!mmm.setValueCallback) {
                mmm.setValueCallback = updateDataValuesOnChange;
            }
            mmm.updateHighlighting();
            mmm.updateLabelPositions(camera);
        } else {
            mmmObjects.delete(mmm);
        }
    }
}

export default MeanMedianMode;
export { MeanMedianMode, updateMMMObjects };

function applyMaterialProperties(root) {
    let materialsMap = new WeakMap();

    let getMaterial = (material) => {
        let newMaterial = materialsMap.get(material);
        if (newMaterial) return newMaterial;

        if (material instanceof THREE.MeshStandardMaterial) {
            newMaterial = material;
        } else {
            newMaterial = new THREE.MeshStandardMaterial();
            for (let prop of MaterialCopyProperties) {
                if (prop in material) newMaterial[prop] = material[prop];
            }
        }

        newMaterial.envMap = AssetLibrary.envLight.activeMap;
        newMaterial.needsUpdate = true;
        materialsMap.set(material, newMaterial);

        return newMaterial;
    };

    root.traverse(obj => {
        let material = obj.material;
        if (material) {
            if (material instanceof Array) {
                for (let i = 0; i < material.length; i++) {
                    material[i] = getMaterial(material[i]);
                }
            } else {
                obj.material = getMaterial(material);
            }
        }
    });
}

function loadBox() {
    let material = MaterialBuilder.build({ color: BOX_COLOR, materialType: "pbr" });
    let hlMaterial = MaterialBuilder.build({ color: BOX_HL_COLOR, materialType: "pbr" });
    let geometry = new THREE.BoxBufferGeometry(BOXMODEL_SIZE, 1, BOXMODEL_SIZE);
    let box = new THREE.Mesh(geometry, material);
    applyMaterialProperties(box);

    return new Promise((resolve, reject) => resolve(box));
}

function loadGltf(url) {
    return new Promise((resolve, reject) => {
        gltfLoader = gltfLoader || new THREE.GLTFLoader();
        gltfLoader.load(url, (gltf) => {
            applyMaterialProperties(gltf.scene);
            resolve(gltf.scene);
        }, undefined, reject);
    });
}

function loadFbx(url) {
    return new Promise((resolve, reject) => {
        fbxLoader = fbxLoader || new FBXLoader();
        fbxLoader.load(url, obj => {

            if (ORIGINAL_WIDTH) { obj.scale.multiplyScalar(0.01); }

            applyMaterialProperties(obj);
            resolve(obj);
        }, undefined, reject);
    });
}